import React, { useState, useEffect } from 'react';
import { Sidebar } from '../../components/Sidebar';
import Chatbot from '../../components/Chatbot';
import ChatSessionList, { ChatSession } from '../../components/ChatSessionList';
import SideChatPanel from '../../components/SideChatPanel';
import { AppConfig } from '../../../types';
import { v4 as uuidv4 } from 'uuid';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { deleteChatSession, getChatTitleHistory, getMainChatHistory, updateChatSessionTitle } from '../../services/chatService';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { addMessages, resetQuestion } from '../../redux/counterSlice';

const MainChat = () => {
  const userId = localStorage.getItem("user_id");
  const sessionId = localStorage.getItem("session_id");
  const params = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  if (!params || !params.id || params.id === 'undefined') {
    navigate(`/chat/new-chat`, { replace: true });
  }

  const [isShowButton, setIsShowButton] = useState<boolean>(false);
  const [test, setTest] = useState<string>("");
  const [errored, setError] = useState<boolean>(false);
  const [followUP, setFollowUP] = useState<boolean>(false);
  const [typePassword, setTypePassword] = useState<boolean>(false);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [config, setConfig] = useState<AppConfig>({ mode: 'hybrid', username: '' });
  const [rename, setRename] = useState<boolean>(false);
  const [wasDelete, setDelete] = useState<boolean>(false);
  const isAuthDetails = useSelector((state: any) => state.persistent.isAuth);
  // Chat session management
  const [chatSessions, setChatSessions] = useState<ChatSession[]>([]);
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
  const [renameDialogVisible, setRenameDialogVisible] = useState<boolean>(false);
  const [deleteDialogVisible, setDeleteDialogVisible] = useState<boolean>(false);
  const [sessionToRename, setSessionToRename] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState<string>('');
  const [sessionToDelete, setSessionToDelete] = useState<string | null>(null);
  const [chatTitle, setChatTitle] = useState<string>('');

  // Fetch sessions from API
  const fetchChatSessions = async () => {
    try {
      const { result, error } = await getChatTitleHistory({ user_id: userId, session_id: sessionId });
      if (result && Array.isArray(result)) {
        setChatSessions(result);
        if (result.length) setActiveSessionId(result[0].conversation_id);
      } else {
        console.error('Failed to fetch chat sessions:', error);
      }
    } catch (err) {
      console.error('Error fetching chat sessions:', err);
    }
  };

  useEffect(() => {
    fetchChatSessions();
  }, [userId, sessionId]);

  // Fetch chat history for a specific conversation
  const fetchMainChatHistory = async () => {
    // Use params.id as conversation_id if available, otherwise use activeSessionId
    const conversationId = params.id && params.id !== "new-chat" ? params.id : activeSessionId;
    
    if (!conversationId || conversationId === "new-chat" || !userId || !sessionId) {
      // Clear messages if no valid conversation_id or missing required IDs
      dispatch(addMessages([]));
      return;
    }

    try {
      const { result, error } = await getMainChatHistory({ 
        user_id: userId, 
        session_id: sessionId, 
        conversation_id: conversationId, 
        page_no: 1, 
        page_size: 5 
      });

      if (error) {
        console.error('Failed to fetch chat history:', error);
        dispatch(addMessages([]));
        return;
      }

      if (result && Array.isArray(result?.records) && result.records.length > 0) {
        // Normalize messages to ensure they have the expected structure
        const normalizedMessages = result.records.map((record: any) => {
          // If the record doesn't have an 'answer' property but has answer data at root level, normalize it
          if (!record.answer && (record.response || record.visualization || record.results)) {
            return {
              ...record,
              answer: {
                response: record.response || record.answer || '',
                visualization: record.visualization || null,
                results: record.results || ''
              }
            };
          }
          // Ensure answer object exists even if empty
          if (!record.answer) {
            return {
              ...record,
              answer: {
                response: record.response || '',
                visualization: null,
                results: ''
              }
            };
          }
          return record;
        });

        // Ensure latest message appears last (bottom of chat)
        // If backend sends newest-first, reverse; otherwise sort by timestamp if available.
        let orderedMessages = [...normalizedMessages];

        if (normalizedMessages.length > 1) {
          const firstHasCreatedAt = (normalizedMessages[0] as any)?.created_at;
          const lastHasCreatedAt = (normalizedMessages[normalizedMessages.length - 1] as any)?.created_at;

          if (firstHasCreatedAt && lastHasCreatedAt) {
            // Sort by created_at ascending so older messages are first, latest is last
            orderedMessages = normalizedMessages.slice().sort((a: any, b: any) => {
              return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
            });
          } else {
            // Fallback: assume API returns newest-first -> reverse to make newest last
            orderedMessages = normalizedMessages.slice().reverse();
          }
        }

        dispatch(addMessages(orderedMessages));
      } else {
        // No history found, clear messages
        dispatch(addMessages([]));
      }
    } catch (err) {
      console.error('Error fetching chat history:', err);
      dispatch(addMessages([]));
    }
  };

  useEffect(() => {
    if (params.id !== undefined && params.id !== "new-chat" && userId && sessionId) {
      fetchMainChatHistory();
    } else if (params.id === "new-chat") {
      // Clear messages for new chat
      dispatch(addMessages([]));
    }
  }, [params.id, userId, sessionId]);


  // Save sessions to localStorage whenever they change
  useEffect(() => {
    if (chatSessions.length > 0) {
      localStorage.setItem('chatSessions', JSON.stringify(chatSessions));
      if (activeSessionId) localStorage.setItem('activeSessionId', activeSessionId);
    }
  }, [chatSessions, activeSessionId]);

  const handleNewAnalysis = () => {
    navigate('/chat/new-chat')
    dispatch(resetQuestion());
    setActiveSessionId("");
  };

  const handleSwitchSession = (conversation_id: string) => setActiveSessionId(conversation_id);
  const handleRenameSession = (conversation_id: string) => {
    const session = chatSessions.find(s => s.conversation_id === conversation_id);
    if (session) {
      setSessionToRename(conversation_id);
      setRenameValue(session.title || '');
      setRenameDialogVisible(true);
    }
  };

  const handleConfirmRename = async () => {
    if (sessionToRename && renameValue.trim()) {
      try {
        const { result, error } = await updateChatSessionTitle({
          user_id: userId,
          session_id: sessionId,
          conversation_id: sessionToRename,
          new_title: renameValue.trim()
        });
        if (result) {
          setChatSessions(prev =>
            prev.map(s =>
              s.conversation_id === sessionToRename
                ? { ...s, title: renameValue.trim() }
                : s
            )
          );
          setRename(true);
        } else {
          console.error('Failed to rename session:', error);
        }
      } catch (err) {
        console.error('Error renaming session:', err);
      }
    }
    setSessionToRename(null);
    setRenameValue('');
    setRenameDialogVisible(false);
  };

  useEffect(() => {
    if (rename || wasDelete) {
      fetchChatSessions();
      setRename(false);
      setDelete(false);
    }
  }, [rename,wasDelete, userId, sessionId]);

  // Delete session handlers
  const confirmDeleteSession = (conversation_id: string) => {
    setSessionToDelete(conversation_id);
    setDeleteDialogVisible(true);
    fetchChatSessions();
  };

  const handleDeleteSession = async () => {
    if (!sessionToDelete) return;

    try {
      const { result, error } = await deleteChatSession({
        user_id: userId,
        session_id: sessionId,
        conversation_id: sessionToDelete
      });

      if (result) {
        const updatedSessions = chatSessions.filter(s => s.conversation_id !== sessionToDelete);
        setChatSessions(updatedSessions);
        setDelete(true);

        if (sessionToDelete === activeSessionId) {
          if (updatedSessions.length > 0) setActiveSessionId(updatedSessions[0].conversation_id);
          else handleNewAnalysis();
        }
      } else {
        console.error('Failed to delete chat session:', error);
      }
    } catch (err) {
      console.error('Error deleting chat session:', err);
    } finally {
      setSessionToDelete(null);
      setDeleteDialogVisible(false);
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    window.location.href = '/login';
  };

  return (
    <div style={{ display: "flex" }}>
      <Sidebar
        userName={`${isAuthDetails?.first_name ?? ''} ${isAuthDetails?.last_name ?? ''}`}
        userRole={isAuthDetails?.group_name}
        onNewAnalysis={handleNewAnalysis}
        onLogout={handleLogout}
      >
        <ChatSessionList
          chatSessions={chatSessions}
          activeSessionId={activeSessionId}
          onSwitchSession={handleSwitchSession}
          onRenameSession={handleRenameSession}
          onDeleteSession={confirmDeleteSession} // Pass confirmation handler
        />
      </Sidebar>

      <Chatbot
        test={test}
        setTest={setTest}
        errored={errored}
        setError={setError}
        followUP={followUP}
        setFollowUP={setFollowUP}
        typePassword={typePassword}
        setTypePassword={setTypePassword}
        conversation_id={params.id}
        chatTitle={chatTitle}
        setChatTitle={setChatTitle}
        fetchChatSessions={fetchChatSessions}
      />

      {/* Rename Dialog */}
      <Dialog
        header="Rename Chat"
        className='rename-chat-dialog'
        visible={renameDialogVisible}
        onHide={() => setRenameDialogVisible(false)}
        modal
        style={{ width: '350px' }}
      >
        <InputText
          value={renameValue}
          className='rename-input'
          onChange={(e) => setRenameValue(e.target.value)}
          autoFocus
          placeholder="Enter chat name"
        />
        <div className="p-dialog-footer">
          <Button label="Cancel" className="p-button-text" onClick={() => setRenameDialogVisible(false)} />
          <Button label="Save" onClick={handleConfirmRename} />
        </div>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        header="Confirm Delete"
        visible={deleteDialogVisible}
        onHide={() => setDeleteDialogVisible(false)}
        modal
        className="delete-chat-dialog"
        style={{ width: "350px" }}
      >
        <p>Are you sure you want to delete this chat?</p>
        <div className="p-dialog-footer">
          <Button
            label="Cancel"
            className="p-button-text"
            onClick={() => setDeleteDialogVisible(false)}
          />
          <Button
            label="Delete"
            className="p-button-danger"
            onClick={handleDeleteSession}
          />
        </div>
      </Dialog>
    </div>
  );
};

export default MainChat;
